import * as React from 'react'

import { ParsleyMark } from '../components/parsley-mark'
import { cn } from '../lib/utils'

/**
 * One plan, on the `/subscribe` chooser. A `role="radio"` button — the whole card is the target,
 * not a hidden input beside a label.
 *
 * ## Both cards are equal citizens
 *
 * This is the constraint the whole component is built around: Parsley does **not** recommend a
 * plan. So neither card may be bigger, brighter, badged, or pre-selected. Both wear the same
 * deep-green head; the only difference is **how much parsley is in it** — two leaves for Pinch,
 * five for Handful. The metaphor carries the size relationship so the styling doesn't have to.
 *
 * Selection is **amber**, the pre-action colour, applied as a ring — never as a bigger or brighter
 * card. "Your current plan" is a factual label, not an endorsement.
 *
 * The one asymmetry is deliberate and *inverted* from the usual pattern: Pinch renders a constant
 * 16px shorter via `self-stretch my-2` inside a stretched grid row, so the row is always
 * `max(Handful, Pinch + 16)`. Because Handful stretches to fill, the delta stays exact at any
 * width and Pinch can never clip. Note it's the **cheaper** card that's smaller — the expensive
 * one isn't being inflated to sell it.
 *
 * ## Presentational
 *
 * Every string arrives pre-formatted and `@parsleyai/ui` is i18n-free. The card does no pricing
 * math and draws no conclusion from the numbers it renders.
 */
export interface TierCardFacts {
  /** Row label, e.g. "Dinners a week". */
  dinnersLabel: string
  /** Pre-formatted value, e.g. "up to 3". */
  dinnersValue: string
  /** How many of the 7 dots are filled. */
  dinnersFilled: number
  /** Row label, e.g. "Recipe effort". */
  effortLabel: string
  /** Pre-formatted value, e.g. "easier to involved". */
  effortValue: string
  /** 0–1, how much of the effort track is filled. */
  effortFraction: number
  /** Track end labels, e.g. ["Easier", "Involved"]. */
  effortEnds: [string, string]
}

export interface TierCardProps {
  /** Public plan name, e.g. "Handful". Not the Stripe tier key. */
  name: string
  /** Pre-formatted, e.g. "$9.99". */
  price: string
  /** e.g. "per month". */
  per: string
  facts: TierCardFacts
  /** Leaf garnish count. Two for the smaller plan, five for the larger — the pinch/handful metaphor. */
  garnish: 'pinch' | 'handful'
  /** Renders one step lighter and 16px shorter. The smaller plan. */
  compact?: boolean
  /** Factual "this is what you're on today" label. Not an endorsement. */
  current?: boolean
  currentLabel?: string
  selected?: boolean
  disabled?: boolean
  /** Roving tabindex — only the selected card (or the first, when none is) is tabbable. */
  tabbable?: boolean
  /** Full sentence for screen readers: name, price, facts, and current-plan status. */
  ariaLabel: string
  onSelect?: () => void
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>
  className?: string
}

/** Leaf positions, in px, relative to the garnish box. A pinch of parsley, and a handful. */
const GARNISH: Record<'pinch' | 'handful', { w: number; x: number; y: number; rot: number; op?: number }[]> = {
  pinch: [
    { w: 36, x: 34, y: 30, rot: -14 },
    { w: 48, x: 78, y: 12, rot: 12 }
  ],
  handful: [
    { w: 32, x: 4, y: 40, rot: -30, op: 0.8 },
    { w: 44, x: 26, y: 8, rot: -12 },
    { w: 56, x: 62, y: 22, rot: 8 },
    { w: 40, x: 100, y: 2, rot: 28, op: 0.9 },
    { w: 32, x: 112, y: 48, rot: 46, op: 0.8 }
  ]
}

// The rings are NAMED shadows in tailwind.preset.cjs (`shadow-tier-selected` / `shadow-tier-focus`)
// rather than arbitrary values. Three attempts silently compiled to *nothing* here: `aria-checked:`
// is not a variant in Tailwind 3.4, and an arbitrary `shadow-[…]` value containing commas
// breaks the parser, with and without `!`. Each time the class landed on the element
// and no rule was generated — the markup read correctly and no ring appeared. The ring is the
// primary affordance on this screen, so it's pinned by a browser assertion rather than trusted.
export const TierCard = React.forwardRef<HTMLButtonElement, TierCardProps>(
  (
    {
      name,
      price,
      per,
      facts,
      garnish,
      compact,
      current,
      currentLabel,
      selected,
      disabled,
      tabbable = true,
      ariaLabel,
      onSelect,
      onKeyDown,
      className
    },
    ref
  ) => {
    return (
      // biome-ignore lint/a11y/useSemanticElements: the whole card is the radio, not a hidden <input> beside a label — an input can't host the head, garnish and diagrams, and a visually-hidden one would put the tap target somewhere other than the thing being tapped. TierGroup supplies the radiogroup, roving tabindex and arrow keys.
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={!!selected}
        data-selected={!!selected}
        aria-label={ariaLabel}
        disabled={disabled}
        tabIndex={tabbable ? 0 : -1}
        onClick={onSelect}
        onKeyDown={onKeyDown}
        className={cn(
          'group relative flex cursor-pointer flex-col overflow-hidden rounded-[20px] border-none bg-card p-0 text-left text-foreground',
          selected ? 'shadow-tier-selected' : 'shadow-card',
          'transition-[box-shadow,transform] duration-200 motion-reduce:transform-none dark:bg-card-sunken',
          'hover:-translate-y-0.5 data-[selected=true]:-translate-y-0.5',
          // Focus outranks selection: a keyboard user needs to see where they *are* more than what
          // they've picked. It must stay a CSS `:focus-visible` utility rather than tracked state —
          // `onFocus` fires on mouse clicks too, which painted the blue focus ring over the amber
          // selection ring for pointer users.
          'focus-visible:outline-none focus-visible:shadow-tier-focus',
          'disabled:cursor-not-allowed disabled:opacity-55',
          // The constant-16px-shorter trick. Reset at <560px, where the cards stack and natural
          // height is correct.
          compact && 'my-2 self-stretch max-[560px]:my-0',
          className
        )}>
        <div
          className={cn(
            'relative rounded-t-[20px] px-[17px] pt-[15px] pb-[17px] text-white',
            // Same green on both heads; the compact card sits one step lighter.
            compact
              ? 'bg-[linear-gradient(118deg,oklch(var(--brand-green-700))_0%,oklch(var(--brand-green-600))_100%)] px-4 pt-3.5 pb-[15px] dark:bg-[linear-gradient(118deg,oklch(var(--brand-green-800))_0%,oklch(var(--brand-green-700))_100%)]'
              : 'bg-[linear-gradient(118deg,oklch(var(--brand-green-800))_0%,oklch(var(--brand-green-700))_100%)] dark:bg-[linear-gradient(118deg,oklch(var(--brand-green-900))_0%,oklch(var(--brand-green-800))_100%)]',
            // 20px dot film, matching the media panel's texture at a smaller pitch.
            'after:pointer-events-none after:absolute after:inset-0 after:[background-image:radial-gradient(circle,rgba(255,255,255,0.06)_1px,transparent_1px)] after:[background-size:20px_20px]'
          )}>
          <span aria-hidden className="pointer-events-none absolute -right-3 -top-2 bottom-0 z-0 w-[150px] text-white opacity-[0.13]">
            {GARNISH[garnish].map((leaf) => (
              <span
                key={`${leaf.x}-${leaf.y}`}
                className="absolute origin-center leading-none"
                style={{ width: leaf.w, left: leaf.x, top: leaf.y, transform: `rotate(${leaf.rot}deg)`, opacity: leaf.op }}>
                <ParsleyMark filled className="block h-auto w-full" />
              </span>
            ))}
          </span>

          <div className="relative z-[1] flex items-start justify-between gap-2.5">
            <div className="flex min-w-0 flex-wrap items-center gap-[9px]">
              <span
                className={cn(
                  'font-brand font-semibold leading-[1.1] tracking-[-0.012em] text-white',
                  compact ? 'text-[21px]' : 'text-2xl'
                )}>
                {name}
              </span>
              {current && currentLabel && (
                <span className="whitespace-nowrap rounded-full border border-white/[0.32] bg-white/[0.14] px-2.5 py-[3px] text-[11px] font-semibold tracking-[0.02em] text-white">
                  {currentLabel}
                </span>
              )}
            </div>
            <span
              aria-hidden
              className={cn(
                'relative mt-px size-[23px] shrink-0 rounded-full border-2 border-white/55 transition-colors duration-150',
                'group-hover:border-white/90 group-data-[selected=true]:border-white'
              )}>
              <i
                className={cn(
                  'absolute inset-1 scale-0 rounded-full bg-amber-400 transition-transform duration-[180ms] ease-[cubic-bezier(.22,.68,0,1.2)]',
                  'group-data-[selected=true]:scale-100 motion-reduce:transition-none'
                )}
              />
            </span>
          </div>

          <div className="relative z-[1] mt-2 flex items-baseline gap-[7px]">
            <b
              className={cn(
                'font-mono font-medium leading-none tracking-[-0.02em] tabular-nums text-white',
                compact ? 'text-[22px]' : 'text-2xl'
              )}>
              {price}
            </b>
            <span className="text-[12.5px] text-brand-green-50">{per}</span>
          </div>
        </div>

        {/* The two variables, drawn identically on both cards — same diagrams, different values, so
          the comparison is like-for-like. aria-hidden because `ariaLabel` already says all of it. */}
        <div aria-hidden className={cn('flex flex-col', compact ? 'flex-1 gap-3 px-4 pt-[15px] pb-4' : 'gap-3.5 px-[17px] pt-4 pb-[18px]')}>
          <div>
            <div className="flex items-baseline justify-between gap-2 text-[12.5px] text-muted-foreground">
              <span>{facts.dinnersLabel}</span>
              <b className="font-semibold text-foreground">{facts.dinnersValue}</b>
            </div>
            <div className="mt-2 flex gap-[5px]">
              {Array.from({ length: 7 }, (_, i) => (
                <span
                  key={`dot-${i}`}
                  className={cn(
                    'size-[11px] rounded-full',
                    i < facts.dinnersFilled
                      ? 'bg-brand-green-600 dark:bg-brand-green-400'
                      : 'bg-[color-mix(in_oklab,oklch(var(--foreground))_13%,transparent)]'
                  )}
                />
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-baseline justify-between gap-2 text-[12.5px] text-muted-foreground">
              <span>{facts.effortLabel}</span>
              <b className="font-semibold text-foreground">{facts.effortValue}</b>
            </div>
            <div className="relative mt-2.5 h-1.5 rounded-full bg-[color-mix(in_oklab,oklch(var(--foreground))_13%,transparent)]">
              <i
                className="absolute inset-y-0 left-0 rounded-full bg-brand-green-600 dark:bg-brand-green-400"
                style={{ width: `${facts.effortFraction * 100}%` }}
              />
            </div>
            <div className="mt-1.5 flex justify-between text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
              <span>{facts.effortEnds[0]}</span>
              <span>{facts.effortEnds[1]}</span>
            </div>
          </div>
        </div>
      </button>
    )
  }
)
TierCard.displayName = 'TierCard'
